import { useState } from "react";
import { Box, Button } from "@mui/material";
import { Layer } from "../../types/layer";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter, FilterAttribute } from "./FilterModal";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface FireHistoryControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  addModalRef?: React.MutableRefObject<{ open: () => void } | null>;
}

const FireHistoryControl: React.FC<FireHistoryControlProps> = ({
  layer,
  filters,
  onFiltersChange,
  expanded,
  onExpandedChange,
  addModalRef,
}) => {
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  const attributes: FilterAttribute[] = layer?.rawSource
    ? [
        { value: "FireName", label: "Fire Name", type: "string" },
        { value: "EndDate", label: "Date", type: "date" },
      ]
    : [];

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  const handleUploadClick = () => {
    addModalRef?.current?.open();
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={handleUploadClick}
        sx={{
          color: "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Upload from File
      </Button>
    </Box>
  );

  return (
    <>
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Fire History"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <LayerControlItem
        title="Fire History"
        icon="local_fire_department"
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        drawingContent={drawingContent}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    </>
  );
};

export default FireHistoryControl;
